import { useEffect, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, Check, CircleDot, Loader2, X } from "@fg/icons";
import type { SaveStatus } from "@/lib/useSaveTracker";
import { cn } from "@/lib/utils";

// Document name + save status for the suite top bar (SuiteAppBar's title
// region). The bar can be Accent, Light or Dark, so nothing here hard-codes
// white: text inherits the bar's colour, secondary text is opacity-80.
// Click the name to rename it inline: Enter / blur commits, Esc cancels.

interface FileTitleProps {
    name: string;
    status: SaveStatus;
    /** Bound to a Drive file — a clean document then reads "Saved to Drive". */
    inDrive: boolean;
    loading: boolean;
    /** Rejects with a user-facing message (e.g. Drive's duplicate-name error). */
    onRename: (next: string) => Promise<void>;
    onRetry: () => void;
    /** Second line under the name — the Drive location breadcrumb. */
    location?: ReactNode;
    /** After the name — the version-history and comments buttons. */
    accessory?: ReactNode;
}

// Legible on every top-bar style (a coloured bar, white or near-black).
const WARN_CHIP = "rounded bg-amber-100 px-1.5 text-amber-900 dark:bg-amber-900/70 dark:text-amber-100";

export function FileTitle({ name, status, inDrive, loading, onRename, onRetry, location, accessory }: FileTitleProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // Enter commits and then unmounts the input, which can also fire blur.
    const settledRef = useRef(false);

    useEffect(() => {
        if (!editing) return;
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        // Select the base name only, as file managers do.
        const dot = input.value.lastIndexOf(".");
        input.setSelectionRange(0, dot > 0 ? dot : input.value.length);
    }, [editing]);

    const begin = () => {
        settledRef.current = false;
        setDraft(name);
        setError(null);
        setEditing(true);
    };

    const cancel = () => {
        settledRef.current = true;
        setEditing(false);
        setError(null);
    };

    const commit = async () => {
        if (settledRef.current || busy) return;
        const next = draft.trim();
        if (!next || next === name) {
            cancel();
            return;
        }
        setBusy(true);
        try {
            await onRename(next);
            settledRef.current = true;
            setEditing(false);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const badge = <SaveStatusBadge status={status} inDrive={inDrive} loading={loading} onRetry={onRetry} />;
    const hasBadge = loading || status !== "clean" || inDrive;
    // Two lines when there's a location or a status to show; otherwise just the name.
    const twoLines = Boolean(location) || hasBadge || Boolean(error);
    const nameHeight = twoLines ? "h-6" : "h-8";

    return (
        <div className="flex min-w-0 flex-col justify-center">
            <div className="flex min-w-0 items-center gap-0.5">
                {editing ? (
                    <input
                        ref={inputRef}
                        value={draft}
                        disabled={busy}
                        aria-label="File name"
                        aria-invalid={error ? true : undefined}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); void commit(); }
                            else if (e.key === "Escape") { e.preventDefault(); cancel(); }
                        }}
                        onBlur={() => void commit()}
                        className={cn(nameHeight, "w-64 max-w-[40vw] rounded border border-input bg-background px-2 text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70")}
                    />
                ) : (
                    <button
                        type="button"
                        onClick={begin}
                        title="Rename"
                        className={cn(nameHeight, "suite-appbar-btn min-w-0 max-w-[40vw] truncate rounded px-1.5 text-sm font-semibold")}
                    >
                        {name}
                    </button>
                )}
                {accessory}
            </div>
            {twoLines ? (
                <div className="flex min-w-0 items-center gap-2 px-1.5 text-xs leading-4">
                    {location}
                    {error ? (
                        <span role="alert" className={cn("truncate", WARN_CHIP)}>
                            {error}
                        </span>
                    ) : (
                        <span aria-live="polite" className="flex shrink-0 items-center">
                            {badge}
                        </span>
                    )}
                </div>
            ) : null}
        </div>
    );
}

/**
 * The top bar's Close (the last editor control, after Share): closes the file
 * and returns to the app's home. `busy` while a save in flight finishes.
 * Follows the bar's colours (Accent / Light / Dark) via suite-appbar-*.
 */
export function CloseFileButton({ appName, busy, onClose }: { appName: string; busy: boolean; onClose: () => void }) {
    return (
        <div className="suite-appbar-divider flex shrink-0 items-center pl-1.5">
            <button
                type="button"
                onClick={onClose}
                disabled={busy}
                aria-label="Close file"
                aria-busy={busy || undefined}
                title={busy ? "Finishing the save before closing…" : `Close file (return to ${appName} home)`}
                className="suite-appbar-btn flex h-8 w-8 items-center justify-center rounded disabled:cursor-wait"
            >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
            </button>
        </div>
    );
}

function SaveStatusBadge({
    status, inDrive, loading, onRetry
}: {
    status: SaveStatus;
    inDrive: boolean;
    loading: boolean;
    onRetry: () => void;
}) {
    const base = "flex items-center gap-1 whitespace-nowrap text-xs";
    const muted = "opacity-80";
    if (loading) {
        return <span className={cn(base, muted)}><Loader2 className="h-3 w-3 animate-spin" aria-hidden />Opening…</span>;
    }
    switch (status) {
        case "saving":
            return <span className={cn(base, muted)}><Loader2 className="h-3 w-3 animate-spin" aria-hidden />Saving…</span>;
        case "failed":
            return (
                <button
                    type="button"
                    onClick={onRetry}
                    className={cn(base, WARN_CHIP, "font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current")}
                >
                    <AlertTriangle className="h-3 w-3" aria-hidden />Save failed — retry
                </button>
            );
        case "dirty":
            return <span className={cn(base, muted)}><CircleDot className="h-3 w-3" aria-hidden />Unsaved changes</span>;
        default:
            return inDrive
                ? <span className={cn(base, muted)}><Check className="h-3 w-3" aria-hidden />Saved to Drive</span>
                : null;
    }
}
